import type {TextWithLinkPart} from './types';

const URL_PATTERN = /(?:https?:\/\/|www\.)[^\s<]+/gi;
const IMAGE_FILE_EXTENSIONS = /\.(?:avif|bmp|gif|heic|heif|jpe?g|png|svg|tiff?|webp)$/i;

const trimTrailingPunctuation = (url: string): string => {
  return url.replace(/[.,;:!?)}\]'»]+$/u, '');
};

const normalizeHref = (url: string): string | null => {
  const trimmedUrl = trimTrailingPunctuation(url);
  const href = trimmedUrl.startsWith('www.') ? `https://${trimmedUrl}` : trimmedUrl;

  try {
    const parsed = new URL(href);

    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.toString();
    }
  } catch {
    return null;
  }

  return null;
};

export const isImageUrl = (href: string): boolean => {
  try {
    const url = new URL(href);

    return (
      (url.protocol === 'http:' || url.protocol === 'https:') &&
      IMAGE_FILE_EXTENSIONS.test(url.pathname)
    );
  } catch {
    return false;
  }
};

export const parseTextWithLinks = (text: string): TextWithLinkPart[] => {
  const parts: TextWithLinkPart[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(URL_PATTERN)) {
    const rawUrl = match[0];
    const index = match.index ?? 0;
    const href = normalizeHref(rawUrl);

    if (!href) {
      continue;
    }

    const displayUrl = trimTrailingPunctuation(rawUrl);

    if (index > lastIndex) {
      parts.push({
        type: 'text',
        value: text.slice(lastIndex, index)
      });
    }

    parts.push({
      type: 'link',
      value: displayUrl,
      href
    });

    lastIndex = index + rawUrl.length;
  }

  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      value: text.slice(lastIndex)
    });
  }

  if (parts.length === 0) {
    return [{type: 'text', value: text}];
  }

  return parts;
};
